/**
 * Edge-triggered scrollbar visibility.
 *
 * Scrollbars are hidden by default (via CSS). When the mouse hovers within
 * a thin zone at the right/bottom edge of a scrollable container for 600ms,
 * the `scrollbar-visible` class is added to reveal the scrollbar.
 *
 * Handles both native scrollbars and Radix ScrollArea (which renders its own
 * scrollbar DOM elements). For Radix, the class is added to the ScrollArea
 * root so the CSS selector `.scrollbar-visible > [data-radix-scroll-area-scrollbar]`
 * can reveal the thumb.
 */

const EDGE_ZONE = 16; // px from the right/bottom edge
const HOVER_DELAY = 250; // ms before scrollbar appears

let hoverTimer: ReturnType<typeof setTimeout> | null = null;
let currentTarget: Element | null = null;

/**
 * Given a scrollable element, returns the element that should receive
 * the `scrollbar-visible` class. For Radix ScrollArea viewports, this
 * is the ScrollArea root (parent). For everything else, it's the element itself.
 */
function getClassTarget(el: Element): Element {
  // Radix ScrollArea viewport has this attribute
  if (el.hasAttribute('data-radix-scroll-area-viewport')) {
    const root = el.closest('[data-radix-scroll-area-root]');
    if (root) return root;
  }
  return el;
}

function findScrollableAncestor(
  el: Element | null,
  clientX: number,
  clientY: number
): Element | null {
  while (el) {
    if (el === document.documentElement || el === document.body) {
      const doc = document.documentElement;
      const hasVerticalScroll = doc.scrollHeight > doc.clientHeight;
      const hasHorizontalScroll = doc.scrollWidth > doc.clientWidth;
      if (hasVerticalScroll || hasHorizontalScroll) {
        const nearRight =
          hasVerticalScroll && clientX >= window.innerWidth - EDGE_ZONE;
        const nearBottom =
          hasHorizontalScroll && clientY >= window.innerHeight - EDGE_ZONE;
        if (nearRight || nearBottom) return doc;
      }
      return null;
    }

    const hasVerticalScroll = el.scrollHeight > el.clientHeight;
    const hasHorizontalScroll = el.scrollWidth > el.clientWidth;

    if (hasVerticalScroll || hasHorizontalScroll) {
      const rect = el.getBoundingClientRect();
      const nearRight = hasVerticalScroll && clientX >= rect.right - EDGE_ZONE;
      const nearBottom =
        hasHorizontalScroll && clientY >= rect.bottom - EDGE_ZONE;
      if (nearRight || nearBottom) return getClassTarget(el);
    }

    el = el.parentElement;
  }
  return null;
}

function clearState() {
  if (hoverTimer) {
    clearTimeout(hoverTimer);
    hoverTimer = null;
  }
  if (currentTarget) {
    currentTarget.classList.remove('scrollbar-visible');
    currentTarget = null;
  }
}

document.addEventListener(
  'mousemove',
  (e: MouseEvent) => {
    const target = findScrollableAncestor(
      e.target as Element,
      e.clientX,
      e.clientY
    );

    if (target === currentTarget) return;

    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
    if (currentTarget) {
      currentTarget.classList.remove('scrollbar-visible');
      currentTarget = null;
    }

    if (!target) return;

    // Start delay for new target
    currentTarget = target;
    hoverTimer = setTimeout(() => {
      if (currentTarget) {
        currentTarget.classList.add('scrollbar-visible');
      }
    }, HOVER_DELAY);
  },
  { passive: true }
);

document.addEventListener('mouseleave', clearState, { passive: true });
